import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import audioBus from '../services/AudioBus';
import { useStreamResilience } from '../hooks/useStreamResilience';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LIVE_STREAM_URL = 'https://djsandmc.media/listen/wnuv/radio.mp3';

const PlayerContext = createContext(null);

export const usePlayer = () => useContext(PlayerContext);

export const PlayerProvider = ({ children }) => {
  const [playingTrack, setPlayingTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tuning, setTuning] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [liveNowPlaying, setLiveNowPlaying] = useState(null);
  const audioRef = useRef(new Audio());
  const previewTimerRef = useRef(null);
  const livePollingRef = useRef(null);
  const audioBusHandle = useRef(null);
  const { isBuffering } = useStreamResilience(audioRef);

  const fetchNowPlaying = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/nowplaying/wnuv`);
      const data = await res.json();
      setLiveNowPlaying({
        title: data.now_playing?.song?.title || 'The NU Vibe',
        artist: data.now_playing?.song?.artist || 'Live',
        art: data.now_playing?.song?.art || null,
        listeners: data.listeners?.current || 0,
      });
    } catch {
      setLiveNowPlaying({ title: 'The NU Vibe', artist: 'Live', art: null, listeners: 0 });
    }
  }, []);

  const switchToLive = useCallback(() => {
    const audio = audioRef.current;
    setTuning(true);
    setIsLive(true);
    audio.pause();
    audio.src = LIVE_STREAM_URL;
    audio.play().catch(() => {});
    setIsPlaying(true);
    fetchNowPlaying();
    clearInterval(livePollingRef.current);
    livePollingRef.current = setInterval(fetchNowPlaying, 15000);
    setTimeout(() => setTuning(false), 1200);
  }, [fetchNowPlaying]);

  const handlePlay = useCallback((track) => {
    const audio = audioRef.current;
    // Register with AudioBus if not already
    if (!audioBusHandle.current) {
      audioBusHandle.current = audioBus.register('global-player', {
        pause: () => { audio.pause(); setIsPlaying(false); }
      });
    }
    // Toggle if same track
    if (playingTrack?.id === track.id) {
      if (isPlaying) { audio.pause(); setIsPlaying(false); }
      else { audioBusHandle.current.claim(); audio.play().catch(() => {}); setIsPlaying(true); }
      return;
    }
    // Claim audio bus — stops all other players
    audioBusHandle.current.claim();
    // Stop live polling
    clearInterval(livePollingRef.current);
    clearTimeout(previewTimerRef.current);
    setIsLive(false);
    setLiveNowPlaying(null);
    setTuning(true);
    setPlayingTrack(track);

    audio.pause();
    const isLiveStation = track.is_live || track.stream_url?.includes('/listen/');
    if (isLiveStation) {
      audio.src = track.stream_url;
      audio.play().catch(() => {});
      setIsPlaying(true);
      setIsLive(true);
      fetchNowPlaying();
      livePollingRef.current = setInterval(fetchNowPlaying, 15000);
      setTimeout(() => setTuning(false), 1200);
    } else {
      audio.src = track.stream_url;
      audio.play().catch(() => {});
      setIsPlaying(true);
      setTimeout(() => setTuning(false), 500);
      // After 60s preview, switch to live
      previewTimerRef.current = setTimeout(() => switchToLive(), 60000);
    }
  }, [playingTrack, isPlaying, fetchNowPlaying, switchToLive]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else {
      if (!audioBusHandle.current) {
        audioBusHandle.current = audioBus.register('global-player', {
          pause: () => { audio.pause(); setIsPlaying(false); }
        });
      }
      audioBusHandle.current.claim();
      audio.play().catch(() => {}); setIsPlaying(true);
    }
  }, [isPlaying]);

  const stopPlayer = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.src = '';
    setIsPlaying(false);
    setPlayingTrack(null);
    setIsLive(false);
    setLiveNowPlaying(null);
    clearInterval(livePollingRef.current);
    clearTimeout(previewTimerRef.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(livePollingRef.current);
      clearTimeout(previewTimerRef.current);
      if (audioBusHandle.current) audioBusHandle.current.unregister();
    };
  }, []);

  const value = {
    playingTrack, isPlaying, tuning, isLive, liveNowPlaying, isBuffering,
    audioRef, handlePlay, togglePlay, stopPlayer,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};
